import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import { useFamily } from '../../context/FamilyContext';
import { uploadDocument } from '@coparent/shared/src/services/documentService';
import { DOCUMENT_CATEGORIES, ALLOWED_FILE_TYPES, MAX_FILE_SIZE_BYTES } from '@coparent/shared/src/utils/constants';
import ChildMultiSelect from '../common/ChildMultiSelect';
import { HiX, HiCloudUpload } from 'react-icons/hi';
import toast from 'react-hot-toast';

const DocumentUpload = ({ onClose }) => {
  const { currentUser } = useAuth();
  const { familyId, familyChildren, childrenMap } = useFamily();
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedChildIds, setSelectedChildIds] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      category: 'other',
    }
  });

  const validateFile = (file) => {
    if (!ALLOWED_FILE_TYPES[file.type]) {
      toast.error('File type not supported. Use images, PDFs, or Word documents.');
      return false;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error('File too large. Maximum size is 10MB.');
      return false;
    }
    return true;
  };

  const handleFileSelect = (file) => {
    if (!file || !validateFile(file)) return;
    setSelectedFile(file);
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
    setValue('name', nameWithoutExt);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const onSubmit = async (data) => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }
    setLoading(true);
    try {
      await uploadDocument(familyId, selectedFile, {
        name: data.name,
        category: data.category,
        childIds: selectedChildIds,
        uploadedBy: currentUser.uid,
        uploadedByName: currentUser.displayName || 'Unknown',
      }, setUploadProgress);
      toast.success('Document uploaded!');
      onClose();
    } catch (error) {
      console.error('Document upload error:', error);
      toast.error('Failed to upload document');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-ink-900">Upload Document</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors">
            <HiX className="text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Drop zone */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
              dragActive
                ? 'border-canopy-400 bg-canopy-50'
                : selectedFile
                  ? 'border-canopy-300 bg-canopy-50/50'
                  : 'border-surface-200 hover:border-ink-300 hover:bg-surface-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={Object.keys(ALLOWED_FILE_TYPES).join(',')}
              onChange={(e) => handleFileSelect(e.target.files?.[0])}
            />
            <HiCloudUpload className={`text-3xl mx-auto mb-2 ${selectedFile ? 'text-canopy-500' : 'text-ink-300'}`} />
            {selectedFile ? (
              <div>
                <p className="text-sm font-semibold text-ink-700">{selectedFile.name}</p>
                <p className="text-xs text-ink-400 mt-0.5">
                  {(selectedFile.size / (1024 * 1024)).toFixed(1)} MB
                </p>
                <p className="text-xs text-canopy-600 mt-1 font-medium">Click or drag to replace</p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-ink-500">
                  Drop a file here or <span className="text-canopy-600 font-semibold">browse</span>
                </p>
                <p className="text-xs text-ink-300 mt-1">Images, PDFs, or Word documents up to 10MB</p>
              </div>
            )}
          </div>

          {/* Upload progress */}
          {loading && (
            <div className="w-full bg-surface-100 rounded-full h-2">
              <div
                className="bg-canopy-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Document Name</label>
            <input
              {...register('name', { required: 'Required' })}
              type="text"
              className="input-base"
              placeholder="e.g. Vaccination record"
            />
            {errors.name && <p className="mt-1.5 text-xs text-accent-600 font-medium">{errors.name.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Category</label>
            <select {...register('category')} className="input-base">
              {DOCUMENT_CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {familyChildren.length > 0 && (
            <ChildMultiSelect
              familyChildren={familyChildren}
              childrenMap={childrenMap}
              selectedChildIds={selectedChildIds}
              onChange={setSelectedChildIds}
            />
          )}

          <div className="flex gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1"
            >
              {loading ? `Uploading ${uploadProgress}%...` : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentUpload;
